import { useState } from 'react';
import { ScrollRevealWrapper } from '../../../components/feature/ScrollRevealWrapper';

const faqs = [
  {
    categoria: 'Sobre coaching e métodos anteriores',
    pergunta: 'Já fiz coaching antes e não funcionou. Por que seria diferente desta vez?',
    resposta: 'Coaching tradicional trabalha com metas e ação externa. O Método Bia Holmes atua em camada mais profunda: reprograma os padrões neurológicos que fazem você sabotar resultados mesmo sabendo o que precisa fazer. Se você já teve clareza e não executou, ou voltou aos mesmos padrões depois de um tempo, o problema não era o coaching — era que a raiz não foi tratada. PNL clínica acessa exatamente isso: o sistema interno que pilota suas decisões, suas reações e seu teto de renda.',
  },
  {
    categoria: 'Sobre tempo e resultados',
    pergunta: 'Quanto tempo leva para ver resultados concretos?',
    resposta: 'A maioria das alunas reporta mudanças perceptíveis em 21 a 30 dias de prática consistente — não meses. Algumas experimentam shifts imediatos após a primeira sessão: tomam uma decisão que adiavam há meses, conseguem se comunicar com mais autoridade em reuniões, eliminam uma reação emocional que repetiam em loop. Para resultados financeiros e de carreira mensuráveis — aumento de renda, promoção, lançamento de negócio — o protocolo recomendado é de 90 dias. Não porque seja lento, mas porque transformações duradouras precisam de consolidação.',
  },
  {
    categoria: 'Sobre tempo e agenda',
    pergunta: 'Minha agenda é extremamente cheia. Terei tempo para isso?',
    resposta: 'O método foi desenhado para executivas que não têm tempo a perder. Cada exercício tem de 8 a 22 minutos, pode ser feito no celular, e funciona melhor em momentos de transição do dia — manhã antes de abrir e-mail, pausa entre reuniões, ou à noite antes de dormir. Não exige blocos grandes de agenda. Na prática, alunas C-Level com agendas de 12h/dia completam o protocolo no mesmo ritmo que as demais.',
  },
  {
    categoria: 'Sobre ceticismo e eficácia',
    pergunta: 'Sou muito analítica e cética. PNL tem base científica?',
    resposta: 'Boa pergunta — e é exatamente o perfil de quem aproveita mais o método. PNL aplicada trabalha com neuroplasticidade, condicionamento clássico (Pavlov aplicado ao comportamento humano) e processamento sensorial — todos com respaldo em neurociência comportamental. O método de Bia Holmes é certificado pelo Society of NLP e Richard Bandler Institute, os organismos mais rigorosos da área. Você não precisa acreditar em nada para que funcione — só precisa executar os protocolos e observar os resultados.',
  },
  {
    categoria: 'Sobre investimento',
    pergunta: 'O investimento é alto para quem ainda não viu resultado.',
    resposta: 'Por isso oferecemos uma entrada sem risco real: o plano gratuito permite experimentar exercícios reais — não versões demonstração — sem cartão de crédito. Nos planos pagos, há garantia incondicional de 7 dias: se você não sentir nenhuma diferença após praticar as técnicas na primeira semana, devolvemos 100% sem questionamento. O risco financeiro é zero. O custo de continuar com os mesmos padrões por mais um ano é a única conta que vale fazer.',
  },
  {
    categoria: 'Sobre perfil',
    pergunta: 'Isso é para mulheres em crise ou em dificuldade? Não é meu caso.',
    resposta: 'Pelo contrário — o método foi construído para quem já tem performance acima da média e quer o próximo salto. A maioria das alunas são executivas sêniores, fundadoras e profissionais liberais com renda de R$ 20k+ que chegam não por estarem mal, mas porque perceberam que chegaram num teto que esforço puro não rompe. O foco é alta performance, autoridade de palco e carreira, e expansão de capacidade de geração de renda — não gestão de crise.',
  },
  {
    categoria: 'Sobre continuidade',
    pergunta: 'Como sei que não vou desistir no meio do caminho?',
    resposta: 'O método é gamificado: cada exercício concluído gera pontos, badges e avanço de nível visível no painel. Há uma comunidade de executivas no mesmo percurso — não um fórum genérico, mas um grupo curado por critério de perfil. Além disso, lembretes inteligentes se adaptam ao seu calendário para manter a consistência sem depender de força de vontade. Alunas que chegam ao 30º dia têm 94% de taxa de conclusão dos 90 dias.',
  },
];

export default function FAQHome() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-28 bg-[#060606] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(201,164,64,0.04),transparent_60%)]"></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
        <ScrollRevealWrapper animation="fade-up">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-4 mb-6">
              <div className="h-px w-10 bg-gradient-to-r from-transparent to-gold-500/60"></div>
              <span className="text-gold-400 text-xs font-semibold tracking-[0.35em] uppercase font-['Montserrat']">
                Suas perguntas respondidas
              </span>
              <div className="h-px w-10 bg-gradient-to-l from-transparent to-gold-500/60"></div>
            </div>
            <h2 className="font-display text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
              As objeções que <span className="text-gold-400">toda executiva tem.</span>
            </h2>
            <p className="text-white/50 text-base font-light leading-relaxed font-['Montserrat'] max-w-xl mx-auto">
              Respostas diretas para quem pensa muito antes de decidir — e está certa em fazer isso.
            </p>
          </div>
        </ScrollRevealWrapper>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <ScrollRevealWrapper key={index} animation="fade-up" delay={0.05 * index}>
              <div className="bg-[#111111] border border-gold-500/10 rounded-xl overflow-hidden hover:border-gold-500/25 transition-all duration-300">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-7 py-5 flex items-start justify-between gap-4 text-left cursor-pointer"
                >
                  <div className="flex-1">
                    <p className="text-gold-500/60 text-[10px] font-semibold tracking-[0.25em] uppercase font-['Montserrat'] mb-1.5">
                      {faq.categoria}
                    </p>
                    <h3 className="text-sm font-semibold text-white leading-snug font-['Montserrat']">
                      {faq.pergunta}
                    </h3>
                  </div>
                  <div className={`w-7 h-7 mt-0.5 flex-shrink-0 flex items-center justify-center rounded-full border border-gold-500/30 text-gold-400 transition-transform duration-300 ${openIndex === index ? 'rotate-180 bg-gold-500/10' : ''}`}>
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </div>
                </button>

                <div className={`overflow-hidden transition-all duration-400 ${openIndex === index ? 'max-h-[500px]' : 'max-h-0'}`}>
                  <div className="px-7 pb-6">
                    <div className="border-l-2 border-gold-500/30 pl-5">
                      <p className="text-white/60 text-sm leading-relaxed font-['Montserrat']">
                        {faq.resposta}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollRevealWrapper>
          ))}
        </div>

        {/* CTA Final */}
        <ScrollRevealWrapper animation="fade-up" delay={0.4}>
          <div className="mt-12 text-center bg-gradient-to-br from-[#1a1400] to-[#0f0c00] border border-gold-500/20 rounded-2xl p-8">
            <p className="text-gold-400/70 text-xs tracking-[0.3em] uppercase font-['Montserrat'] font-semibold mb-3">
              Ainda tem dúvidas?
            </p>
            <h3 className="font-display text-2xl font-bold text-white mb-3">
              Nossa equipe responde em <span className="text-gold-400">menos de 24h.</span>
            </h3>
            <p className="text-white/40 text-sm mb-7 font-['Montserrat']">
              Nada de chatbot. Uma pessoa real, que conhece o método.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="mailto:contato@biaholmes.com"
                className="px-7 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-[#0a0a0a] font-bold rounded-full hover:from-gold-600 hover:to-gold-800 transition-all text-sm whitespace-nowrap font-['Montserrat']"
              >
                <i className="ri-mail-line mr-2"></i>
                Enviar email
              </a>
              <a
                href="/diagnostico"
                className="px-7 py-3 bg-transparent border border-gold-500/30 text-gold-400 font-semibold rounded-full hover:bg-gold-500/8 transition-all text-sm whitespace-nowrap font-['Montserrat']"
              >
                <i className="ri-brain-line mr-2"></i>
                Fazer diagnóstico gratuito
              </a>
            </div>
          </div>
        </ScrollRevealWrapper>
      </div>
    </section>
  );
}
